import { useLayoutEffect, useRef, useState, type ChangeEvent, type CompositionEvent, type FocusEvent } from 'react';
import { TextInput, type TextInputProps } from '@carbon/react';
import { formatVnd } from '@ruby-cms/shared';
import { chuanHoa, viTri } from './oTien.ts';

// O nhap tien dung chung cua CMS: hien dau cham hang nghin, trang thai cua trang chi giu chu so.
// - bo go (IME, goi y chu cua Windows, Unikey) dang soan: hien dung chuoi trinh duyet dang giu, chua dinh dang lai
//   (viet lai o giua luc soan lam Chrome chen lai chu so vua go: 2223000 thanh 22.233.000.000), xong soan moi dinh dang;
// - con tro giu sau dung ngan ay chu so; xoa trung dau cham thi xoa chu so ke ben; qua maxDigits thi bo lan go;
// - onChange(chu so) chay o moi lan go, ke ca luc dang soan, nen so gui di luon la so dang hien.
type Props = Omit<TextInputProps, 'value' | 'defaultValue' | 'onChange'> & {
  value: string | number | null | undefined;
  onChange: (digits: string) => void;
  maxDigits?: number;
};

const chuSo = (v: Props['value']) => (typeof v === 'number' ? (Number.isFinite(v) ? String(Math.trunc(Math.abs(v))) : '') : (v ?? '').replace(/\D/g, ''));
const dinhDang = (d: string) => (d ? formatVnd(BigInt(d)) : '');

export function MoneyInput({ value, onChange, maxDigits = 15, onBlur, onCompositionStart, onCompositionEnd, ...rest }: Props) {
  const so = chuSo(value);
  const ref = useRef<HTMLInputElement>(null);
  const soan = useRef(false);
  const truocSoan = useRef<string | null>(null); // chu so truoc khi bo go bat dau soan (luc soan trang co the giu so qua dai)
  const conTro = useRef<number | null>(null);
  const [nhap, setNhap] = useState<string | null>(null);
  const [, setLuot] = useState(0);

  // dinh dang lai chuoi trong o va dat con tro ngay (React thay o da dung gia tri nen khong viet lai, con tro o yen)
  function ap(el: HTMLInputElement, inputType: string) {
    const r = chuanHoa(el.value, el.selectionStart ?? el.value.length, truocSoan.current ?? so, inputType, maxDigits);
    truocSoan.current = null;
    const hien = dinhDang(r.digits);
    if (el.value !== hien) el.value = hien;
    if (document.activeElement === el) { const p = viTri(hien, r.left); el.setSelectionRange(p, p); }
    conTro.current = r.left;
    setNhap(null);
    setLuot((n) => n + 1); // luon ve lai, ke ca khi so khong doi (lan go bi tu choi, xoa trung dau cham)
    if (r.digits !== so) onChange(r.digits);
  }

  useLayoutEffect(() => {
    const el = ref.current;
    const left = conTro.current;
    conTro.current = null;
    if (left === null || !el || document.activeElement !== el || nhap !== null) return;
    const p = viTri(el.value, left);
    if (el.selectionStart !== p || el.selectionEnd !== p) el.setSelectionRange(p, p);
  });

  function doi(e: ChangeEvent<HTMLInputElement>) {
    const ne = e.nativeEvent as InputEvent;
    if (soan.current || ne.isComposing) {
      const el = e.target;
      setNhap(el.value);
      // luc soan khong cat theo maxDigits: trang giu dung chu so dang hien; xong soan moi tu choi lan go qua dai
      const r = chuanHoa(el.value, el.value.length, so, '', Infinity);
      if (r.digits !== so) onChange(r.digits);
      return;
    }
    ap(e.target, ne.inputType ?? '');
  }

  return (
    <TextInput {...rest} ref={ref} inputMode={rest.inputMode ?? 'numeric'} autoComplete={rest.autoComplete ?? 'off'}
      value={nhap ?? dinhDang(so)} onChange={doi}
      onCompositionStart={(e: CompositionEvent<HTMLInputElement>) => { soan.current = true; truocSoan.current ??= so; onCompositionStart?.(e); }}
      onCompositionEnd={(e: CompositionEvent<HTMLInputElement>) => { soan.current = false; ap(e.currentTarget, ''); onCompositionEnd?.(e); }}
      onBlur={(e: FocusEvent<HTMLInputElement>) => { if (soan.current || nhap !== null) { soan.current = false; ap(e.currentTarget, ''); } onBlur?.(e); }} />
  );
}
